package com.islamicmedia.shared.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.islamicmedia.shared.data.auth.AuthState
import com.islamicmedia.shared.data.auth.AuthUser
import com.islamicmedia.shared.data.auth.GoogleSignInCancelledException
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import com.islamicmedia.shared.platform.Analytics
import com.islamicmedia.shared.platform.Funnel
import com.islamicmedia.shared.platform.FunnelStep
import com.islamicmedia.shared.platform.Event
import com.islamicmedia.shared.platform.Param
import com.islamicmedia.shared.util.currentTimeMillis
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AuthUiState(
    val isLoading: Boolean = true,
    val isAuthenticated: Boolean = false,
    val isApproved: Boolean = false,
    val isCheckingApproval: Boolean = false,
    val user: AuthUser? = null,
    val error: String? = null,
    // Form state
    val email: String = "",
    val password: String = "",
    val firstName: String = "",
    val lastName: String = "",
    val isSignUp: Boolean = true,   // Sign Up is the default auth tab (new-user-first)
    val isSubmitting: Boolean = false,
    // Email-code verification modal (post-signup OTP)
    val awaitingVerification: Boolean = false,
    val verificationEmail: String? = null,
    val verificationCode: String = "",
    val verificationNotice: String? = null   // transient status, e.g. "Code resent"
)

class AuthViewModel : ViewModel() {
    private val authService = SupabaseAuthService.instance

    private val _uiState = MutableStateFlow(AuthUiState())
    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()

    /** Account we've already counted a sign-up for, so the repeated `Authenticated` emissions
     *  (token refresh, app foreground) can't count the same registration twice. */
    private var signUpCountedForUserId: String? = null

    init {
        observeAuthState()
    }

    /**
     * Count a first-ever Google/Apple registration.
     *
     * Email sign-up fires [Event.SIGN_UP] at its own call sites, but OAuth never fired it at all —
     * only [Event.LOGIN], on the button tap — so a brand-new Google user was indistinguishable
     * from a returning one, and registrations read as 1 against 71 installs in a month.
     *
     * There is still no OAuth success callback (see [signInWithGoogle]), so infer it: the session
     * carries the ACCOUNT's creation time, and an account created moments ago is a registration.
     *
     * Skips "email" deliberately — that path already counts itself, and counting again here would
     * double every email sign-up.
     */
    private fun trackSignUpIfNewOAuthAccount(user: AuthUser) {
        val provider = user.provider ?: return
        if (provider.equals("email", ignoreCase = true)) return
        if (user.id.isBlank() || user.id == signUpCountedForUserId) return
        val createdAt = user.createdAtEpochMs ?: return
        // Upper bound only. A createdAt in the "future" just means the device clock trails the
        // server's, and that account is certainly fresh — rejecting it would drop real sign-ups on
        // skewed devices. The window is wide enough to survive a slow browser OAuth round-trip and
        // far short of the ~1h token refresh that would otherwise re-count the same account.
        if (currentTimeMillis() - createdAt > NEW_ACCOUNT_WINDOW_MS) return
        signUpCountedForUserId = user.id
        Funnel.track(Event.SIGN_UP, FunnelStep.SIGNED_UP, mapOf(Param.METHOD to provider))
    }

    private companion object {
        /** How recently an account must have been created to count this session as its sign-up. */
        const val NEW_ACCOUNT_WINDOW_MS = 5 * 60 * 1000L
    }

    private fun observeAuthState() {
        viewModelScope.launch {
            try {
                authService.authState.collect { state ->
                    when (state) {
                        is AuthState.Loading -> {
                            _uiState.update { it.copy(isLoading = true) }
                        }
                        is AuthState.Authenticated -> {
                            // Before the branching below: both paths return early, and a brand-new
                            // OAuth account has to be counted on whichever one runs.
                            trackSignUpIfNewOAuthAccount(state.user)
                            // Approval gate retired under payments — the credit ledger gates access,
                            // so every authenticated user is "approved". Skipping checkApproval here
                            // also avoids a mid-session churn loop (token refresh → re-check →
                            // isCheckingApproval toggle → screen remount → refresh → …).
                            if (com.islamicmedia.shared.AppConfig.PAYMENTS_ENABLED) {
                                _uiState.update { it.copy(
                                    isLoading = false,
                                    isAuthenticated = true,
                                    isApproved = true,
                                    isCheckingApproval = false,
                                    user = state.user
                                ) }
                                return@collect
                            }
                            // If already approved in this session, skip re-checking.
                            // Token refreshes emit new Authenticated events — re-querying
                            // approved_users on each one risks a transient failure flipping
                            // isApproved to false and showing Access Denied mid-session.
                            val alreadyApproved = _uiState.value.isApproved
                            if (alreadyApproved) {
                                _uiState.update { it.copy(
                                    isLoading = false,
                                    isAuthenticated = true,
                                    user = state.user
                                ) }
                            } else {
                                _uiState.update { it.copy(
                                    isLoading = false,
                                    isAuthenticated = true,
                                    isCheckingApproval = true,
                                    user = state.user
                                ) }
                                // Check if user is approved
                                val email = state.user.email
                                val approved = if (email != null) {
                                    authService.checkApproval(email)
                                } else {
                                    false
                                }
                                _uiState.update { it.copy(
                                    isApproved = approved,
                                    isCheckingApproval = false
                                ) }
                            }
                        }
                        is AuthState.NotAuthenticated -> {
                            _uiState.update { it.copy(
                                isLoading = false,
                                isAuthenticated = false,
                                isApproved = false,
                                isCheckingApproval = false,
                                user = null
                            ) }
                        }
                    }
                }
            } catch (e: Throwable) {
                _uiState.update { it.copy(isLoading = false, isAuthenticated = false) }
            }
        }
    }

    fun onEmailChanged(email: String) {
        _uiState.update { it.copy(email = email, error = null) }
    }

    fun onPasswordChanged(password: String) {
        _uiState.update { it.copy(password = password, error = null) }
    }

    fun onFirstNameChanged(firstName: String) {
        _uiState.update { it.copy(firstName = firstName, error = null) }
    }

    fun onLastNameChanged(lastName: String) {
        _uiState.update { it.copy(lastName = lastName, error = null) }
    }

    fun toggleSignUp() {
        _uiState.update { it.copy(isSignUp = !it.isSignUp, error = null) }
    }

    fun onSubmit() {
        val state = _uiState.value
        if (state.email.isBlank() || state.password.isBlank()) {
            _uiState.update { it.copy(error = "Please fill in all fields") }
            return
        }

        if (state.isSignUp && (state.firstName.isBlank() || state.lastName.isBlank())) {
            _uiState.update { it.copy(error = "Please enter your first and last name") }
            return
        }

        if (state.password.length < 6) {
            _uiState.update { it.copy(error = "Password must be at least 6 characters") }
            return
        }

        _uiState.update { it.copy(isSubmitting = true, error = null) }

        viewModelScope.launch {
            if (state.isSignUp) {
                val fullName = "${state.firstName.trim()} ${state.lastName.trim()}"
                val result = authService.signUpWithEmail(state.email, state.password, fullName)
                when {
                    // Confirmations OFF → a real session exists → straight into the app.
                    authService.hasActiveSession -> {
                        Funnel.track(Event.SIGN_UP, FunnelStep.SIGNED_UP, mapOf(Param.METHOD to "email"))
                        _uiState.update { it.copy(isSubmitting = false, email = "", password = "", firstName = "", lastName = "") }
                    }
                    // No session = email confirmation required → show the code modal.
                    // (Covers both the explicit email_not_confirmed failure and any
                    //  SDK "success" that doesn't actually establish a session.)
                    result.isSuccess || (result.exceptionOrNull()?.message?.contains("email_not_confirmed", ignoreCase = true) == true) -> {
                        _uiState.update { it.copy(
                            isSubmitting = false, error = null,
                            awaitingVerification = true, verificationEmail = state.email,
                            verificationCode = "", verificationNotice = null,
                        ) }
                    }
                    else -> {
                        _uiState.update { it.copy(isSubmitting = false, error = parseAuthError(result.exceptionOrNull()?.message, true)) }
                    }
                }
            } else {
                authService.signInWithEmail(state.email, state.password).fold(
                    onSuccess = {
                        Funnel.track(Event.LOGIN, params = mapOf(Param.METHOD to "email"))
                        _uiState.update { it.copy(isSubmitting = false, email = "", password = "", firstName = "", lastName = "") }
                    },
                    onFailure = { error ->
                        val msg = error.message?.lowercase() ?: ""
                        if (msg.contains("email_not_confirmed")) {
                            // Unconfirmed account tried to sign in → send a code and open the modal.
                            viewModelScope.launch { authService.resendSignupCode(state.email) }
                            _uiState.update { it.copy(
                                isSubmitting = false, error = null,
                                awaitingVerification = true, verificationEmail = state.email,
                                verificationCode = "", verificationNotice = null,
                            ) }
                        } else {
                            _uiState.update { it.copy(isSubmitting = false, error = parseAuthError(error.message, false)) }
                        }
                    }
                )
            }
        }
    }

    fun onVerificationCodeChanged(code: String) {
        // 6-digit OTP — keep the Supabase "Email OTP Length" set to 6 to match.
        _uiState.update { it.copy(verificationCode = code.filter { c -> c.isDigit() }.take(6), error = null) }
    }

    /** Verify the emailed 6-digit code. On success authState flips to Authenticated → main screen. */
    fun verifyCode() {
        val state = _uiState.value
        val email = state.verificationEmail ?: return
        if (state.verificationCode.length < 6) {
            _uiState.update { it.copy(error = "Enter the 6-digit code") }
            return
        }
        _uiState.update { it.copy(isSubmitting = true, error = null) }
        viewModelScope.launch {
            authService.verifyEmailCode(email, state.verificationCode).fold(
                onSuccess = {
                    // Reaching a verified code always means the account was just confirmed, so this
                    // is the true "new user" moment when email confirmations are ON.
                    Funnel.track(Event.SIGN_UP, FunnelStep.SIGNED_UP, mapOf(Param.METHOD to "email"))
                    _uiState.update { it.copy(
                        isSubmitting = false, awaitingVerification = false,
                        verificationCode = "", verificationEmail = null, verificationNotice = null,
                    ) }
                },
                onFailure = {
                    _uiState.update { it.copy(isSubmitting = false, error = "Invalid or expired code") }
                }
            )
        }
    }

    fun resendCode() {
        val email = _uiState.value.verificationEmail ?: return
        _uiState.update { it.copy(verificationNotice = "Sending…", error = null) }
        viewModelScope.launch {
            val ok = authService.resendSignupCode(email).isSuccess
            _uiState.update { it.copy(verificationNotice = if (ok) "Code resent" else "Couldn't resend. Try again shortly") }
        }
    }

    fun cancelVerification() {
        _uiState.update { it.copy(
            awaitingVerification = false, verificationCode = "",
            verificationEmail = null, verificationNotice = null, error = null,
        ) }
    }

    /** [redirectUrl] — see SupabaseAuthService.signInWithGoogle. Web callers on a page that carries
     *  meaningful query state (e.g. /invite?t=…) must pass their own URL or it is lost in the OAuth
     *  round-trip. Null = platform default. */
    fun signInWithGoogle(redirectUrl: String? = null) {
        // For OAuth, the browser redirects away, so we just start the process
        // Don't show submitting state since the page will redirect
        // Analytics note: OAuth hands off to the browser and comes back through authState, which
        // also fires on every token refresh — so there is no success callback we could count here
        // without inflating logins on each app launch. We count the ATTEMPT instead; a small
        // number of abandoned Google/Apple flows are included in these two methods.
        Funnel.track(Event.LOGIN, params = mapOf(Param.METHOD to "google"))
        viewModelScope.launch {
            val result = authService.signInWithGoogle(redirectUrl)
            result.onFailure { error ->
                // Dismissing the native account sheet is a choice, not a failure — leave the
                // screen untouched. (Only mobile can produce this; the browser flow can't.)
                if (error is GoogleSignInCancelledException) return@onFailure
                _uiState.update {
                    it.copy(error = parseAuthError(error.message, false))
                }
            }
            // Native sheet: the session already exists here and authState has emitted.
            // Browser flow: redirected to Google - no UI update needed.
        }
    }

    fun signInWithApple() {
        // For OAuth, the browser redirects away, so we just start the process
        // Don't show submitting state since the page will redirect
        // See signInWithGoogle for why this counts the attempt rather than the return.
        Funnel.track(Event.LOGIN, params = mapOf(Param.METHOD to "apple"))
        viewModelScope.launch {
            val result = authService.signInWithApple()
            result.onFailure { error ->
                _uiState.update {
                    it.copy(error = parseAuthError(error.message, false))
                }
            }
            // On success, browser redirects to Apple - no UI update needed
        }
    }

    fun recheckApproval() {
        val email = _uiState.value.user?.email ?: return
        _uiState.update { it.copy(isCheckingApproval = true) }
        viewModelScope.launch {
            val approved = authService.checkApproval(email)
            _uiState.update { it.copy(isApproved = approved, isCheckingApproval = false) }
        }
    }

    fun signOut() {
        viewModelScope.launch {
            authService.signOut()
        }
    }

    fun deleteAccount() {
        _uiState.update { it.copy(isSubmitting = true, error = null) }
        viewModelScope.launch {
            val result = authService.deleteAccount()
            result.fold(
                onSuccess = {
                    _uiState.update { it.copy(isSubmitting = false) }
                },
                onFailure = { error ->
                    _uiState.update {
                        it.copy(
                            isSubmitting = false,
                            error = "Failed to delete account. Please try again."
                        )
                    }
                }
            )
        }
    }

    fun dismissError() {
        _uiState.update { it.copy(error = null) }
    }

    fun resetPassword() {
        val email = _uiState.value.email
        if (email.isBlank()) {
            _uiState.update { it.copy(error = "Please enter your email first") }
            return
        }

        viewModelScope.launch {
            val result = authService.resetPassword(email)
            result.fold(
                onSuccess = {
                    _uiState.update { it.copy(error = "Password reset email sent!") }
                },
                onFailure = { error ->
                    _uiState.update { it.copy(error = parseAuthError(error.message, false)) }
                }
            )
        }
    }

    private fun parseAuthError(message: String?, isSignUp: Boolean): String {
        val msg = message?.lowercase() ?: return "Authentication failed. Please try again."

        return when {
            msg.contains("email_address_invalid") || msg.contains("invalid email") ->
                "Please enter a valid email address"
            msg.contains("invalid_credentials") || msg.contains("invalid login") ->
                "Invalid email or password"
            msg.contains("email_not_confirmed") ->
                "Please check your email to confirm your account"
            msg.contains("user_already_exists") || msg.contains("already registered") ->
                "An account with this email already exists"
            msg.contains("weak_password") || msg.contains("password") && msg.contains("weak") ->
                "Password is too weak. Use at least 6 characters"
            msg.contains("rate_limit") || msg.contains("too many requests") ->
                "Too many attempts. Please wait a moment and try again"
            msg.contains("network") || msg.contains("connection") ->
                "Network error. Please check your connection"
            msg.contains("signup_disabled") ->
                "Sign ups are currently disabled"
            else -> message ?: if (isSignUp) "Sign up failed. Please try again." else "Sign in failed. Please try again."
        }
    }
}
